import { useState, type MouseEvent, type ReactNode } from 'react';
import { Avatar } from '../../atoms/Avatar/Avatar';
import { Icon } from '../../atoms/Icon/Icon';
import { Text } from '../../atoms/Text/Text';
import { Popover, popoverAnchorClass } from '../../primitives/Popover/Popover';
import styles from './SidebarFooter.module.css';

/**
 * Figma: `Sidebar Footer` (48:2934), holding `_sidebar footer` (48:2877) —
 * "User profile & actions area. Variants: State=default | hover | active.
 * Positioned at bottom of sidebar."
 *
 * `active` is not a selection state here — it is the open state of the menu,
 * which is why opening the popover also applies the active surface.
 *
 * **Reworked again 2026-09-21.** The panel is a profile menu, no longer a
 * workspace switcher:
 *
 * 1. **Above the trigger**, the `Settings` row is gone (it is still in the
 *    frame, hidden). The `AI credits` card took its place and has since moved
 *    on into the panel, so the slot is empty in the file today — `Help centre`
 *    is the only thing left there and it is hidden too. `items` stays because
 *    the slot and its `space/12` gap are still drawn.
 * 2. **The panel** (`sidebar footer menu`, 2130:51323) is three bands: a
 *    profile header — `_sidebar menu item media` `Size=md, Type=Placeholder`
 *    beside the name and email — then `_sidebar item` rows, then the `Bottom
 *    panel`. The `Switch workspace` list and `Add workspace` are gone with it
 *    (the user's call: switching is removed, not moved).
 *
 * **Reworked again 2026-09-22**, both in the panel:
 *
 * 1. **`Sign out` is a row**, the fourth `_sidebar item` after Settings, Invite
 *    member and FAQ, carrying the `logout` glyph. It was a tertiary `Button`
 *    filling the `Bottom panel`; it is an ordinary menu row now, so it arrives
 *    through `menu` with the others rather than through a prop of its own.
 * 2. **The `Bottom panel` holds the `AI credits` card** instead, on the
 *    `surface/neutral-subtle` band that was already there.
 * 3. The panel widened from 200 to 224.
 */

export type SidebarFooterState = 'default' | 'hover' | 'active';

/** Figma's profile header — the signed-in person, not the workspace. */
export type SidebarFooterProfile = {
  name: string;
  email: string;
  /** Drawn on the placeholder tile, e.g. `JD`. */
  initials: string;
  /**
   * A photo in place of the initials — the one set on Settings → Account.
   * Not in this component's Figma, which only draws the placeholder; the tile
   * is `_sidebar menu item media` either way, so a photo is its `Type=Image`
   * at the same `md` size, cropped to fill rather than letterboxed.
   */
  photoSrc?: string;
};

export type SidebarFooterProps = {
  /** Workspace or account name on the trigger. Truncates when it overflows. */
  name: string;
  /**
   * The workspace logo. The trigger's tile shows the signed-in person's photo
   * instead when `profile.photoSrc` is set (the user's call, 2026-10-26), and
   * the person's initials when there is neither — never an empty tile.
   */
  avatarSrc: string;
  /** What sits above the trigger — Figma puts the `AI credits` card here. */
  items?: ReactNode;
  /** The panel's profile header. Omitted, the band is not rendered. */
  profile?: SidebarFooterProfile;
  /**
   * The panel's rows — typically `SidebarItem`s. Following any link or button
   * among them closes the menu.
   */
  menu?: ReactNode;
  /**
   * Figma's lower `Bottom panel` — the `AI credits` card. Omitted, the band is
   * not rendered.
   */
  credits?: ReactNode;
  /** Controlled open state. Leave undefined to let the component manage it. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Force a state for documentation and visual regression only. */
  state?: SidebarFooterState;
  className?: string;
};

export function SidebarFooter({
  name,
  avatarSrc,
  items,
  profile,
  menu,
  credits,
  open,
  onOpenChange,
  state,
  className,
}: SidebarFooterProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = open !== undefined;
  const isOpen = state === 'active' ? true : isControlled ? open : uncontrolledOpen;

  const setOpen = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  /* A row did its job — navigated or opened something — so the menu goes. */
  const closeOnAction = (event: MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('a, button')) setOpen(false);
  };

  const surface = [
    styles.trigger,
    isOpen || state === 'hover' ? styles.raised : null,
    state === undefined ? styles.interactive : null,
  ]
    .filter(Boolean)
    .join(' ');

  const hasPanel = profile !== undefined || menu !== undefined || credits !== undefined;

  return (
    <div className={[styles.footer, className].filter(Boolean).join(' ')}>
      {items}

      <div className={[styles.triggerWrapper, popoverAnchorClass].join(' ')}>
        <button
          className={surface}
          type="button"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          onClick={() => setOpen(!isOpen)}
        >
          {profile?.photoSrc ? (
            <Avatar src={profile.photoSrc} alt="" size="sm" fit="cover" />
          ) : avatarSrc ? (
            <Avatar src={avatarSrc} alt="" size="sm" />
          ) : (
            <Avatar initials={profile?.initials ?? ''} size="sm" />
          )}
          <span className={styles.name}>{name}</span>
          <Icon name="expand" />
        </button>

        {hasPanel && (
          <Popover
            open={isOpen}
            onClose={() => setOpen(false)}
            placement="right-end"
            label={`${name} menu`}
          >
            <div className={styles.menuWrapper}>
              {profile !== undefined && (
                /* Figma's first `Bottom panel` — the profile header. */
                <div className={styles.profile}>
                  {profile.photoSrc ? (
                    <Avatar src={profile.photoSrc} alt="" size="md" fit="cover" />
                  ) : (
                    <Avatar initials={profile.initials} size="md" />
                  )}
                  <span className={styles.identity}>
                    <Text size="sm" leading="none" weight="medium" color="primary">
                      {profile.name}
                    </Text>
                    <Text size="sm" leading="none" weight="regular" color="tertiary">
                      {profile.email}
                    </Text>
                  </span>
                </div>
              )}

              {menu !== undefined && (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- keyboard is handled by the composite widget (roving focus / native control); the pointer handler is a convenience. Axe checks run in Storybook and E2E.
                <div className={styles.menuItems} onClick={closeOnAction}>
                  {menu}
                </div>
              )}

              {credits !== undefined && <div className={styles.bottomPanel}>{credits}</div>}
            </div>
          </Popover>
        )}
      </div>
    </div>
  );
}
